import { requestMasterTypeConfig, RequestMasterTypeConfigEntry } from "@/Config/RequestMasterTypeConfig";
import { FieldValues } from "react-hook-form";
import { PublicRequestOutput } from "@/Schemas/PublicRequest/PublicRequestOutput";
import { PublicRequestValidateOutput } from "@/Schemas/PublicRequest/PublicRequestValidateOutput";
import { RequestMasterTypeEnum } from "@/Schemas/PublicRequest/RequestMasterTypeEnum";
import { getRecaptchaSiteKey } from "@/Utils/environment";
import { useForm, UseFormReturn } from "@/Hooks/useForm";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import React, { createContext, MutableRefObject, useCallback, useContext, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useEffect } from "react";
import { usePageTitle } from "@/Hooks/usePageTitle";

export type PublicRequestTokenStep = "detail" | "success";

export type PublicRequestProviderState = {
    findContractInput: FieldValues | null;
    contractData: PublicRequestValidateOutput | null;
    requestMasterType: RequestMasterTypeEnum | null;
    masterTypeConfig: RequestMasterTypeConfigEntry | undefined;
    navigationHeadline: string | undefined;
    tokenStep: PublicRequestTokenStep;
    isSuccessStep: boolean;
    publicRequestResult: PublicRequestOutput | null;
    searchRequestMasterType: RequestMasterTypeEnum | null;
    findContractForm: UseFormReturn<FieldValues>;
    recaptchaRef: MutableRefObject<ReCAPTCHA | null>;
    getRecaptchaCode: () => Promise<string>;
    setFindContractInput: (input: FieldValues | null) => void;
    setContractData: (data: PublicRequestValidateOutput | null) => void;
    setRequestMasterType: (type: RequestMasterTypeEnum) => void;
    setTokenStep: (step: PublicRequestTokenStep) => void;
    setPublicRequestResult: (result: PublicRequestOutput | null) => void;
    setSearchRequestMasterType: (type: RequestMasterTypeEnum) => void;
};

const PublicRequestProviderContext = createContext<PublicRequestProviderState>(
    {} as PublicRequestProviderState
);

export type PublicRequestProviderProps = {
    children: React.ReactNode;
};

export const PublicRequestProvider = ({ children }: PublicRequestProviderProps) => {
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const recaptchaResolver = useRef<((code: string | null) => void) | null>(null);
    const recaptchaSiteKey = getRecaptchaSiteKey();

    const [findContractInput, setFindContractInput] = useState<FieldValues | null>(null);
    const [contractData, setContractDataState] = useState<PublicRequestValidateOutput | null>(null);
    const [requestMasterType, setRequestMasterType] = useState<RequestMasterTypeEnum | null>(null);
    const [tokenStep, setTokenStep] = useState<PublicRequestTokenStep>("detail");
    const [publicRequestResult, setPublicRequestResult] = useState<PublicRequestOutput | null>(null);
    const [searchRequestMasterType, setSearchRequestMasterType] = useState<RequestMasterTypeEnum | null>(null);

    const setContractData = useCallback((data: PublicRequestValidateOutput | null) => {
        setContractDataState(data);
        if (data) {
            setRequestMasterType(data.requestMasterType);
            setTokenStep("detail");
            setPublicRequestResult(null);
        }
    }, []);

    const masterTypeConfig =
        requestMasterType !== null ? requestMasterTypeConfig[requestMasterType] : undefined;

    const navigationHeadline = masterTypeConfig?.title;

    const findContractForm = useForm<FieldValues>({
        resolver: masterTypeConfig
            ? zodResolver(masterTypeConfig.formSchema)
            : undefined,

        defaultValues:
            masterTypeConfig?.formDefaultValues ?? {},

        mode: "onChange",
        reValidateMode: "onChange",
    });

    useEffect(() => {
        if (masterTypeConfig) {
            findContractForm.reset(
                masterTypeConfig.formDefaultValues
            );
        }
    }, [masterTypeConfig]);

    const isSuccessStep = tokenStep === "success";

    const getRecaptchaCode = useCallback((): Promise<string> => {
        if (!recaptchaRef.current || !recaptchaSiteKey) {
            throw new Error("reCAPTCHA is not available");
        }

        return new Promise((resolve, reject) => {
            recaptchaRef.current?.reset();
            recaptchaResolver.current = (code: string | null) => {
                if (code) {
                    resolve(code);
                } else {
                    reject(new Error("reCAPTCHA verification failed"));
                }
            };
            recaptchaRef.current?.execute();
        });
    }, []);

    const providerState: PublicRequestProviderState = {
        findContractInput,
        contractData,
        requestMasterType,
        masterTypeConfig,
        navigationHeadline,
        tokenStep,
        isSuccessStep,
        publicRequestResult,
        searchRequestMasterType,
        findContractForm,
        recaptchaRef,
        getRecaptchaCode,
        setFindContractInput,
        setContractData,
        setRequestMasterType,
        setTokenStep,
        setPublicRequestResult,
        setSearchRequestMasterType,
    };

    usePageTitle(masterTypeConfig?.title ?? "Žiadosti");

    return (
        <PublicRequestProviderContext.Provider value={providerState}>
            {children}

            {recaptchaSiteKey && (
                <Box
                    aria-hidden="true"
                    sx={{
                        opacity: 0,
                        pointerEvents: "none",
                        position: "absolute",
                        width: 0,
                        height: 0,
                        overflow: "hidden",
                    }}
                >
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={recaptchaSiteKey}
                        size="invisible"
                        tabIndex={-1}
                        onChange={(recaptchaResponse) => {
                            if (recaptchaResolver.current) {
                                recaptchaResolver.current(recaptchaResponse);
                                recaptchaResolver.current = null;
                            }
                        }}
                    />
                </Box>
            )}
        </PublicRequestProviderContext.Provider>
    );
};

export const usePublicRequestProvider = () => {
    const context = useContext(PublicRequestProviderContext);

    if (!context) {
        throw new Error("usePublicRequestProvider must be used within a PublicRequestProvider");
    }

    return context;
};
